import React from "react";
import { useNavigate } from "react-router";
import { ArrowLeftRight, ArrowRight, MapPin, Plus } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Switch } from "../../../components/ui/switch";
import { cn } from "../../../lib/utils";
import { useNotification } from "../../../contexts/NotificationContext";
import { usePricing } from "../../../contexts/PricingContext";
import { formatCurrency, formatPaxRange } from "../../../lib/pricing";
import { setActive } from "../../pricing/repository";
import { EndpointLabel, describeRoute } from "../../pricing/endpoints";
import { PRICING_PATH } from "../../forms/ZoneForm";
import { Item } from "../settings/Item";
import ListActions from "./ListActions";
import { useDeleteItem } from "./useDeleteItem";

function Endpoint({ endpoint }: { endpoint: EndpointLabel }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      {endpoint.kind === "place" && <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />}
      <span className={cn("truncate", endpoint.kind === "any" && "text-white/60")}>{endpoint.name}</span>
      {endpoint.detail && (
        <span className="hidden truncate text-xs font-normal text-white/40 sm:inline">({endpoint.detail})</span>
      )}
    </span>
  );
}

export default function RoutesTab() {
  const { routes, zones, data } = usePricing();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { requestDelete, dialog } = useDeleteItem("routes", "Tarifa");

  const toggle = async (id: string, active: boolean) => {
    try {
      await setActive("routes", id, active);
    } catch {
      showNotification("error", "Não foi possível alterar o status.");
    }
  };

  return (
    <Item className="space-y-4">
      {routes.length === 0 ? (
        <p className="text-sm text-white/60">Nenhuma tarifa cadastrada.</p>
      ) : (
        <div className="space-y-2">
          {routes.map((route) => {
            const { origin, destination } = describeRoute(data, route);
            const label = `${origin.name} → ${destination.name}`;
            return (
              <article
                key={route.id}
                className={cn(
                  "flex items-start gap-3 rounded-2xl border p-3 transition",
                  route.active ? "border-white/10 bg-white/5" : "border-white/5 bg-white/[0.02] opacity-70"
                )}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => navigate(`${PRICING_PATH}/routes/edit/${route.id}`)}
                >
                  <p className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
                    <Endpoint endpoint={origin} />
                    {route.bidirectional ? (
                      <ArrowLeftRight className="h-4 w-4 shrink-0 text-primary" />
                    ) : (
                      <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
                    )}
                    <Endpoint endpoint={destination} />
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {route.tiers.map((tier, index) => (
                      <span key={index} className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs text-white/90">
                        {formatPaxRange(tier.minPax, tier.maxPax)} pax ·{" "}
                        <strong>{formatCurrency(tier.price)}</strong>
                      </span>
                    ))}
                  </div>
                </button>
                <div className="flex flex-col items-end gap-1">
                  <Switch
                    checked={route.active}
                    aria-label={`Ativar tarifa ${label}`}
                    onCheckedChange={(checked) => toggle(route.id, checked)}
                  />
                  <ListActions
                    onEdit={() => navigate(`${PRICING_PATH}/routes/edit/${route.id}`)}
                    onDelete={() => requestDelete(route.id, label)}
                  />
                </div>
              </article>
            );
          })}
        </div>
      )}
      <Button
        variant="outline"
        className="w-full"
        disabled={zones.length === 0}
        onClick={() => navigate(`${PRICING_PATH}/routes/add`)}
      >
        <Plus className="h-4 w-4" /> Adicionar tarifa
      </Button>
      {dialog}
    </Item>
  );
}
